import {
  Alert,
  AlertDescription,
  Badge,
  Input,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@design-system';
import { EmailTag, useEmailTagInput } from '@/features/invite-client';

import { InfoIcon, TriangleAlert, X } from 'lucide-react';

interface EmailTagInputProps {
  value: EmailTag[];
  onChange: (emails: EmailTag[]) => void;
  maxEmails?: number;
  label?: string;
  placeholder?: string;
}

export function EmailTagInput({
  value: emails,
  onChange,
  maxEmails = 35,
  label = 'Client email(s)',
  placeholder = 'Add one or multiple emails',
}: EmailTagInputProps) {
  const {
    inputValue,
    setInputValue,
    editingValue,
    setEditingValue,
    editingIndex,
    duplicateWarning,
    hasReachedLimit,
    inputRef,
    removeEmail,
    commitEdit,
    startEditing,
    cancelEditing,
    handleInputKeyDown,
    handlePaste,
    handleInputBlur,
  } = useEmailTagInput({ emails, onChange, maxEmails });

  return (
    <TooltipProvider>
      <div className="space-y-2">
        <Label>{label}</Label>
        <div
          className={cn(
            'border-input focus-within:ring-ring/25 focus-within:border-ring flex max-h-60 w-full flex-wrap gap-2 overflow-y-auto rounded-md border px-2.5 py-2 focus-within:ring-[3px]',
          )}
        >
          {emails.map((email, idx) => (
            <Tooltip key={email.value + idx}>
              <TooltipTrigger asChild>
                {editingIndex === idx ? (
                  <Input
                    value={editingValue}
                    onChange={e => setEditingValue(e.target.value)}
                    onBlur={commitEdit}
                    className="h-7 w-max border-0 p-0 text-sm focus-visible:ring-0"
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitEdit();
                      }
                      if (e.key === 'Escape') {
                        cancelEditing();
                      }
                    }}
                  />
                ) : (
                  <Badge
                    variant={email.isValid ? 'secondary' : 'destructive'}
                    onClick={() => startEditing(idx, email.value)}
                    className="cursor-pointer"
                  >
                    <span className="max-w-40 truncate pl-1">{email.value}</span>
                    <X
                      className="size-3 cursor-pointer"
                      onClick={e => {
                        e.stopPropagation();
                        removeEmail(email.value);
                      }}
                    />
                  </Badge>
                )}
              </TooltipTrigger>
              {!email.isValid && (
                <TooltipContent>
                  <p>Invalid email</p>
                </TooltipContent>
              )}
            </Tooltip>
          ))}

          {editingIndex === null && (
            <Input
              ref={inputRef}
              placeholder={hasReachedLimit ? `Maximum ${maxEmails} emails allowed` : placeholder}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onPaste={handlePaste}
              onBlur={handleInputBlur}
              className="h-7 min-w-40 flex-1 border-0 p-0 text-sm focus-visible:ring-0"
              disabled={hasReachedLimit}
            />
          )}
        </div>
        <div className="text-muted-foreground flex items-center gap-1">
          <InfoIcon className="size-3" />
          <p className="text-xs">Press Enter or comma after each email.</p>
        </div>
        {duplicateWarning && (
          <Alert variant="warning">
            <TriangleAlert />
            <AlertDescription>{duplicateWarning}</AlertDescription>
          </Alert>
        )}
        {hasReachedLimit && (
          <Alert variant="warning">
            <TriangleAlert />
            <AlertDescription>Maximum {maxEmails} emails allowed</AlertDescription>
          </Alert>
        )}
      </div>
    </TooltipProvider>
  );
}
